import ModalSectionHeader from "@/components/ui/ModalSectionHeader";
import sectionStyles from "@/asserts/styles/formSection.module.less";
import { Button, Col, Form, Input, Row, Table, TableColumnsType } from "antd";
import { HighValuePurchaseRequisitionFormValue } from "../../type";
import { SupplierUserSimpleListItem } from "@/types/apis";
import { useState } from "react";
import { Trash2 } from "lucide-react";
import { PlusOutlined } from "@ant-design/icons";
import styles from "./SourcingResultFormFields.module.less";
import { SearchSupplier } from "../SearchSupplier";
import { useFormEditable } from "../../hooks/useFormEditable";
import { MaterialFileFormFields } from "@/components/commonFormItems/MaterialFileFormFields";
import { useModal } from "@/hooks/modals/useModal";
import { usePurchaseRequestDetail } from "../../hooks/usePurchaseRequest";
import { useExistFlowHistory } from "@/hooks/services/useFlow";
import {
  financialFirstAuditNodeCodes,
  uploadContractNodeCodes,
} from "../../constant";
import { useTranslation } from "react-i18next";

export function SourcingResultFormFields() {
  const { t } = useTranslation("purchaseRequestManagement");
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);
  const form = Form.useFormInstance<HighValuePurchaseRequisitionFormValue>();

  const { props } = useModal("PURCHASE_REQUEST");
  const { recordId } = props;
  const detail = usePurchaseRequestDetail(recordId);

  /** 是否显示投资分析文件/合并评分表文件 */
  const shouldShowInvestmentAnalysisFiles = useExistFlowHistory(
    financialFirstAuditNodeCodes,
    detail?.insId,
  );

  /** 是否显示合同文件字段 */
  const shouldShowContractFiles = useExistFlowHistory(
    uploadContractNodeCodes,
    detail?.insId,
  );

  const {
    isPurchasingStaffFormFieldsDisabled,
    isFinancialFirstAuditFormFieldsDisabled,
    isUploadContractFormFieldsDisabled,
    isPurchaserAddOrUpdateFilesFormFieldsForHighDisabled,
  } = useFormEditable();

  const sourcingResultFormDisabled =
    isPurchasingStaffFormFieldsDisabled &&
    isPurchaserAddOrUpdateFilesFormFieldsForHighDisabled;

  const onSelectSupplierModalClose = (
    pickedSuppliers?: SupplierUserSimpleListItem[],
  ) => {
    if (pickedSuppliers) {
      let originSupplierList = form.getFieldValue("supplierList") || [];
      originSupplierList = originSupplierList.filter(
        (item: any) => !!item.supplierName,
      );

      const nextSupplierList = [
        ...originSupplierList,
        ...pickedSuppliers.map((item) => ({
          supplierCode: item.supplierCode,
          supplierName: item.supplierName || item.supplierUsName,
          quotationFiles: null,
        })),
      ];

      form.setFieldsValue({
        supplierList: nextSupplierList,
      });
    }
  };

  return (
    <div className={sectionStyles.formSection}>
      <ModalSectionHeader title={t("寻源结果")} />

      <div className={sectionStyles.sectionBody}>
        {!sourcingResultFormDisabled && (
          <SearchSupplier onSelect={onSelectSupplierModalClose} />
        )}
        <Form.List name="supplierList">
          {(fields, { add, remove }) => {
            const columns = createColumns({
              hoverIndex,
              remove,
              disabled: sourcingResultFormDisabled,
              t,
            });
            return (
              <>
                <Table
                  bordered
                  dataSource={fields}
                  pagination={false}
                  scroll={{ x: 800 }}
                  onRow={(_, index) => ({
                    onMouseEnter: () => setHoverIndex(index!),
                    onMouseLeave: () => setHoverIndex(null),
                  })}
                  columns={columns}
                ></Table>

                <Button
                  hidden={sourcingResultFormDisabled}
                  disabled={false}
                  icon={<PlusOutlined />}
                  onClick={add}
                  variant="outlined"
                  color="primary"
                  className={styles.addBtn}
                >
                  {t("继续添加")}
                </Button>
              </>
            );
          }}
        </Form.List>

        <Row>
          <Col span={24} className={styles.fileItem}>
            <MaterialFileFormFields
              fieldName="vendorComparisonSummaryFiles"
              label={t("供应商比较总结商务标评分表")}
              maxCount={5}
              isCustomDisabled={true}
              disabled={isPurchasingStaffFormFieldsDisabled}
              hiddenRemove={isPurchasingStaffFormFieldsDisabled}
              required={false}
            />
          </Col>
        </Row>

        {shouldShowInvestmentAnalysisFiles && (
          <Row>
            <Col span={24} className={styles.fileItem}>
              <MaterialFileFormFields
                fieldName="consolidatedScoringFiles"
                label={t("合并评分表")}
                maxCount={5}
                isCustomDisabled={true}
                disabled={isFinancialFirstAuditFormFieldsDisabled}
                hiddenRemove={isFinancialFirstAuditFormFieldsDisabled}
                required={false}
              />
            </Col>

            <Col span={24} className={styles.fileItem}>
              <MaterialFileFormFields
                fieldName="investmentAnalysisFiles"
                label={t("投资分析")}
                maxCount={5}
                isCustomDisabled={true}
                disabled={isFinancialFirstAuditFormFieldsDisabled}
                hiddenRemove={isFinancialFirstAuditFormFieldsDisabled}
                required={false}
              />
            </Col>
          </Row>
        )}

        {shouldShowContractFiles && (
          <Row>
            <Col span={24} className={styles.fileItem}>
              <MaterialFileFormFields
                fieldName="contractFiles"
                label={t("合同文件")}
                maxCount={1}
                accept=".pdf"
                isCustomDisabled={true}
                disabled={isUploadContractFormFieldsDisabled}
                hiddenRemove={isUploadContractFormFieldsDisabled}
                required={true}
              />
            </Col>
          </Row>
        )}
      </div>
    </div>
  );
}

type CreateColumnsFn = (params: {
  hoverIndex: number | null;
  remove: (index: number) => void;
  disabled: boolean;
  t: (key: string) => string;
}) => TableColumnsType;

const createColumns: CreateColumnsFn = ({
  hoverIndex,
  remove,
  disabled,
  t,
}) => {
  const columns: TableColumnsType = [
    {
      title: t("序号"),
      dataIndex: "index",
      width: 80,
      align: "center",
      fixed: "left",
      render: (_, __, index) => {
        const isHover = hoverIndex === index;
        const canRemove = isHover && !disabled;
        return canRemove ? (
          <div className={styles.removeIcon} onClick={() => remove(index)}>
            <Trash2 size={20} />
          </div>
        ) : (
          index + 1
        );
      },
    },
    {
      title: t("供应商编号"),
      dataIndex: "supplierCode",
      align: "center",
      render: (_, field) => (
        <>
          <Form.Item<HighValuePurchaseRequisitionFormValue["supplierList"]>
            name={[field.name, "id"]}
            noStyle
          ></Form.Item>
          <Form.Item<HighValuePurchaseRequisitionFormValue["supplierList"]>
            name={[field.name, "supplierCode"]}
            noStyle
          >
            <Input placeholder={t("请输入")} disabled={disabled} />
          </Form.Item>
        </>
      ),
    },
    {
      title: <span className={styles.required}>{t("供应商名称")}</span>,
      dataIndex: "supplierName",
      align: "center",
      render: (_, field) => (
        <>
          <Form.Item<HighValuePurchaseRequisitionFormValue["supplierList"]>
            name={[field.name, "supplierName"]}
            rules={[
              {
                required: true,
                message: t("请输入供应商名称"),
              },
            ]}
            noStyle
          >
            <Input placeholder={t("请输入")} disabled={disabled} />
          </Form.Item>
        </>
      ),
    },
    {
      title: t("报价单"),
      dataIndex: "quotationFiles",
      width: 500,
      render: (_, field: any) => (
        <div style={{ marginBottom: -24 }}>
          <MaterialFileFormFields
            parentFieldName="supplierList"
            fieldName={[field.name, "quotationFiles"]}
            label=""
            maxCount={5}
            isCustomDisabled={true}
            disabled={disabled}
            required={false}
            hiddenRemove={disabled}
          />
        </div>
      ),
    },
  ];

  return columns;
};
